import { motion, AnimatePresence } from 'framer-motion';
import { FileText, MessageSquare, X } from 'lucide-react';
import type { PeerData } from '../types';

interface PeerActionMenuProps {
  isOpen: boolean;
  peer: PeerData | null;
  onClose: () => void;
  onSendFile: () => void;
  onSendMessage: () => void;
}

export function PeerActionMenu({
  isOpen,
  peer,
  onClose,
  onSendFile,
  onSendMessage,
}: PeerActionMenuProps) {
  if (!peer) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          />

          {/* Menu */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', duration: 0.3 }}
              className="w-full max-w-sm bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white font-bold shadow-lg">
                    {peer.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {peer.name}
                    </h3>
                    <p className="text-xs text-gray-400">
                      {peer.discoveryMethod === 'local' ? '📶 Same Network' : '🔗 Via Link'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800 rounded-lg"
                  aria-label="Close menu"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Actions */}
              <div className="p-4 space-y-2">
                <button
                  onClick={() => {
                    onSendFile();
                    onClose();
                  }}
                  className="w-full flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <FileText size={24} className="text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <h4 className="text-base font-semibold text-white">Send File</h4>
                    <p className="text-sm text-gray-400">
                      Share a file from your device
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => {
                    onSendMessage();
                    onClose();
                  }}
                  className="w-full flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <MessageSquare size={24} className="text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <h4 className="text-base font-semibold text-white">Send Message</h4>
                    <p className="text-sm text-gray-400">
                      Share a text message
                    </p>
                  </div>
                </button>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-800/50 border-t border-gray-700">
                <button
                  onClick={onClose}
                  className="w-full px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium rounded-lg transition-all"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
